import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';

/** The selectable tabs in the Work View (the `/orders` container page). */
export type WorkViewPage = 'Orders' | 'Plan' | 'Manifest' | 'Legs' | 'Bookings';

export const WORK_VIEW_PAGES: readonly WorkViewPage[] = [
  'Orders',
  'Plan',
  'Manifest',
  'Legs',
  'Bookings',
];

interface WorkViewState {
  currentPage: WorkViewPage;
  setCurrentPage: (page: WorkViewPage) => void;
}

const STORAGE_KEY = 'my:tms:workView';

/** Only the data is persisted — never the action. */
type PersistedWorkView = Pick<WorkViewState, 'currentPage'>;

function isWorkViewPage(value: string): value is WorkViewPage {
  return (WORK_VIEW_PAGES as readonly string[]).includes(value);
}

/**
 * Custom storage that persists the tab as a bare string value
 * (`my:tms:workView = Manifest`), matching the convention used by the other
 * per-app caches (`my:tms:lng`, `my:tms:account_id`) rather than wrapping it in
 * Zustand's default `{"state":{...},"version":0}` envelope.
 *
 * Reads are validated against the known tab set — an unknown / stale value is
 * dropped so the store falls back to the `Orders` default instead of rendering
 * an invalid page.
 */
const storage: PersistStorage<PersistedWorkView> = {
  getItem: (name) => {
    const raw = localStorage.getItem(name);
    if (!raw || !isWorkViewPage(raw)) return null;
    return { state: { currentPage: raw } };
  },
  setItem: (name, value) => {
    localStorage.setItem(name, value.state.currentPage);
  },
  removeItem: (name) => localStorage.removeItem(name),
};

/**
 * Persists the active Work View tab so a refresh / return visit lands the user
 * back on the same page. All tabs live under the single `/orders` route, so the
 * URL can't carry this — we stash it in localStorage under `my:tms:workView`.
 */
export const useWorkViewStore = create<WorkViewState>()(
  persist(
    (set) => ({
      currentPage: 'Orders',
      setCurrentPage: (page) => set({ currentPage: page }),
    }),
    {
      name: STORAGE_KEY,
      storage,
      partialize: (s) => ({ currentPage: s.currentPage }),
    },
  ),
);
